import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { getProfiles, saveProfile, removeProfile } from "@/lib/profiles";
import { claimStoredReferral } from "@/lib/referral";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, object = auth, false = not
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState(getProfiles());
  const bootstrapped = useRef(false);

  const refreshProfiles = useCallback(() => setProfiles(getProfiles()), []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      // Backfill the active account into the saved-profiles list.
      const token = localStorage.getItem("riq_token");
      if (token) {
        saveProfile(data, token);
        setProfiles(getProfiles());
      }
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth, let AuthCallback establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    if (bootstrapped.current) return; // guard against StrictMode double-invoke / re-renders
    bootstrapped.current = true;
    checkAuth();
    // Safety net: never leave the app stuck on the loading spinner if a request hangs.
    const t = setTimeout(() => setLoading(false), 8000);
    return () => clearTimeout(t);
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.session_token) {
      localStorage.setItem("riq_token", data.session_token);
      saveProfile(data.user, data.session_token);
      refreshProfiles();
    }
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, name, accountType = "personal") => {
    const { data } = await api.post("/auth/register", { email, password, name, account_type: accountType });
    if (data.session_token) {
      localStorage.setItem("riq_token", data.session_token);
      saveProfile(data.user, data.session_token);
      refreshProfiles();
    }
    setUser(data.user);
    try { await claimStoredReferral(api); } catch { /* best-effort */ }
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    if (user?.user_id) removeProfile(user.user_id);
    localStorage.removeItem("riq_token");
    refreshProfiles();
    setUser(false);
  };

  // Switch to another saved account without a password. Reloads on success.
  const switchProfile = async (profile) => {
    try {
      const { data } = await api.post("/auth/activate", { session_token: profile.token });
      localStorage.setItem("riq_token", data.session_token);
      saveProfile(data.user, data.session_token);
      window.location.assign("/");
    } catch (e) {
      // Saved session no longer valid — forget it.
      removeProfile(profile.user_id);
      refreshProfiles();
      throw e;
    }
  };

  const refresh = checkAuth;

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, refresh, profiles, switchProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export { formatApiErrorDetail };
